import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PermissionGrid } from "@/components/PermissionGrid";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ShieldCheck } from "lucide-react";

export default function Permissions() {
  const qc = useQueryClient();
  const { data: boot } = useQuery({ queryKey: ["team-boot"], queryFn: async () => (await api.get("/team/bootstrap")).data });
  const { data: perms } = useQuery({ queryKey: ["team-perms"], queryFn: async () => (await api.get("/team/permissions")).data });
  const [gm, setGm] = useState({});
  const [mgr, setMgr] = useState({});
  const [ownDept, setOwnDept] = useState(false);

  useEffect(() => {
    if (perms) { setGm(perms.gm || {}); setMgr(perms.manager || {}); setOwnDept(!!perms.managers_own_departments_only); }
  }, [perms]);

  const keys = boot?.permission_keys || [];
  const save = useMutation({
    mutationFn: async () => (await api.put("/team/permissions", { gm, manager: mgr, managers_own_departments_only: ownDept })).data,
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); qc.invalidateQueries({ queryKey: ["team-perms"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const Toggle = ({ on, onClick, testid }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`h-6 w-11 rounded-full transition-colors ${on ? "bg-[#6E7A63]" : "bg-[#D8D2C6]"}`}>
      <span className={`block h-5 w-5 bg-white rounded-full shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Team &amp; Time</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><ShieldCheck className="h-7 w-7" /> Δικαιώματα</h1>
        <p className="text-[#6F6A60] text-sm mt-1">Ο Ιδιοκτήτης έχει τα πάντα· ο Εργαζόμενος βλέπει μόνο τα δικά του. Ρύθμισε Γενικό Διευθυντή & Διευθυντή.</p>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 flex items-center justify-between">
        <div>
          <div className="font-medium text-[#45423C] text-sm">Οι Διευθυντές φτιάχνουν πρόγραμμα μόνο για το τμήμα τους</div>
        </div>
        <Toggle on={ownDept} onClick={() => setOwnDept(v => !v)} testid="perm-owndept" />
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1EDE6] text-[11px] uppercase tracking-wider text-[#6F6A60]">
              <th className="px-4 py-3 text-left">Δικαίωμα</th>
              <th className="px-4 py-3 w-32 text-center">Γενικός Διευθυντής</th>
              <th className="px-4 py-3 w-28 text-center">Διευθυντής</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(({ key, label }) => (
              <tr key={key} className="border-t border-[#E3DED4]" data-testid={`perm-row-${key}`}>
                <td className="px-4 py-3 text-[#45423C]">{label}</td>
                <td className="px-4 py-3"><div className="flex justify-center"><Toggle on={!!gm[key]} onClick={() => setGm(s => ({ ...s, [key]: !s[key] }))} testid={`perm-gm-${key}`} /></div></td>
                <td className="px-4 py-3"><div className="flex justify-center"><Toggle on={!!mgr[key]} onClick={() => setMgr(s => ({ ...s, [key]: !s[key] }))} testid={`perm-mgr-${key}`} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button data-testid="perm-save" onClick={() => save.mutate()} disabled={save.isPending}
        className="rounded-xl bg-[#45423C] text-white px-6 py-3 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-60">
        {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
      </button>
      <PermissionGrid />
    </div>
  );
}
